import './index.scss';
import ConfigurableInfoBlock, {ConfigurableInfoBlockFeedbackIconPositions} from "../../../../../../components/app-specific/configurable-info-block";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, DialogActions, DialogContent, DialogTitle, Divider, Skeleton, Tooltip, Typography} from "@mui/material";
import {ReactComponent as EnterIcon} from "../../../../../../../assets/images/products/enter.svg";
import ProductChipsUPCContent from "../../../../../../components/app-specific/chips-contents/product-code-content";
import {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {Col, Row} from "reactstrap";
import Form from "../../../../../../components/base/form";
import Button from "../../../../../../components/base/button/button";
import Dialog from "../../../../../../components/base/dialog";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Api from "../../../../../../../core/services/api/api";
import CopyToClipboard from "../../../../../../components/base/copy-to-clipboard";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ContentCopy} from "@mui/icons-material";
import ToastService from "../../../../../../../core/services/toast";
import Regexes from "../../../../../../../core/models/regexes";
import Utils from "../../../../../../../core/services/utils/utils";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";

const initialEditUpcDialogState = {
    open: false,
    upc: null,
    onEdit: () => void 0,
    onClose: () => void 0,
}

const ProductInformationUPCsViewUPCs = ({productId, loading: loadingProps, splitter, setPartUPC}) => {
    const [upcs, setUpcs] = useState([]);
    const [loadingUpcs, setLoadingUpcs] = useState(true);
    const [removingIds, setRemovingIds] = useState([]);
    const [creating, setCreating] = useState(false);
    const [settingAsDefault, setSettingAsDefault] = useState();
    const [editUpcDialogState, setEditUpcDialogState] = useState(initialEditUpcDialogState);
    const dispatch = useDispatch();
    const partnerInformation = usePartnerInformation();
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingUpcs, [loadingProps, loadingUpcs])

    /**
     * Fetches the code groups from the server.
     * @type {(function(): void)}
     */
    const getProductUPCs = useCallback(async () => {
        setLoadingUpcs(true);
        const response = await Api.listPartUpcs(productId);
        if (!isMounted())
            return;
        setLoadingUpcs(false);
        if (response?.resultFlag) {
            setUpcs(response.data?.map(e => ({
                ...e,
                isDefault: e.default ?? false,
            })) ?? []);
        }
    }, [isMounted, productId])

    /**
     * As soon as the component is mounted:
     * - Fetches the code groups.
     */
    useEffect(() => {
        void getProductUPCs();
    }, [getProductUPCs])

    /**
     * Checks if the keywords contain any errors and returns the error message if they do.
     * @type {function(*): [string|null,*|string]}
     */
    const keywordsError = useCallback((keywords) => {
        /**
         * Checks if the string contains special characters
         * @type {function(string=): boolean}
         */
        const containsSpecialCharacters = (str) => {
            str = str?.trim() ?? "";
            return str.match(Regexes.noSpecialCharacters) !== null;
        };

        const errors = [];
        let fixedKeywords = keywords?.trim() ?? "";

        const skipSpecialCharacterError = Utils.skipSpecialCharacterError(partnerInformation?.id);
        if (!skipSpecialCharacterError && containsSpecialCharacters(keywords)) {
            errors.push("The provided UPC contain special characters and cannot be created.");
            fixedKeywords = fixedKeywords.replaceAll(Regexes.specialCharacters, '');
        }

        const errorString = errors.join(' ').trim();
        return [!!errorString ? errorString : null, fixedKeywords];
    }, [partnerInformation?.id])

    /**
     * Creates a product UPC.
     * @type {function(*, *): {data: {isDefault: boolean, code, id: number}, resultFlag: boolean}}
     */
    const createUPC = useCallback(async (codeString) => {
        const codes = codeString.split(splitter).map(e => e.trim()).filter(e => !!e);
        const newUPCs = [];
        for (const code of codes) {
            if (upcs.some(e => e.code.trim() === code.trim())) {
                continue;
            }
            newUPCs.push(code);
        }
        if (!newUPCs) {
            return {
                resultFlag: false,
                message: 'The provided UPCs already exist in the group.',
            }
        }
        const erroneousUPCs = newUPCs.filter(code => {
            const [error] = keywordsError(code);
            return !!error;
        })
        const correctUPCs = newUPCs.filter(e => !erroneousUPCs.includes(e));
        if (erroneousUPCs.length) {
            ToastService.warning({
                title: `Special Characters`,
                message: `
                <div>
                    The following UPCs contain special characters and cannot be created:
                    <ul>
                        ${erroneousUPCs.map(e => (`<li>${e}</li>`)).join('')}
                    </ul>
                </div>
                `,
            })
            if (!correctUPCs.length) {
                return {
                    resultFlag: false,
                    message: 'The provided UPCs contain special characters and cannot be created.',
                }
            }
        }
        setCreating(true);
        let success = false;
        for (const newUPC of correctUPCs) {
            const response = await Api.addPartUpc({
                partId: productId,
                upcCode: newUPC,
            })
            if (!isMounted())
                return;
            if (response?.resultFlag && !!response.data) {
                success = true;
                setUpcs(prevState => [
                    ...prevState,
                    {
                        id: response.data.partUpcId,
                        code: response.data.upcCode,
                        isDefault: response.data.isDefaultUpcForPart ?? false,
                    },
                ])
            }
        }
        setCreating(false);
        return success ? {resultFlag: true} : {resultFlag: false, message: "Error while creating the provided code(s)."}
    }, [splitter, upcs, keywordsError, isMounted, productId])

    /**
     * Removes a product UPC.
     * @type {(function(*, *): void)}
     */
    const removeUPC = useCallback((upc) => {
        return new Promise(r => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
                open: true,
                data: null,
                removedEntryTitle: `UPC: "${upc.code}"`,
                onClose: async (confirmed) => {
                    if (!confirmed) {
                        r(false);
                        return;
                    }
                    setRemovingIds([...removingIds, upc.id]);
                    const response = await Api.removePartUpc({partUpcId: upc.id})
                    if (!isMounted()) {
                        r(false);
                        return;
                    }
                    setRemovingIds(prevState => prevState.filter(id => id !== upc.id));
                    if (response?.resultFlag) {
                        setUpcs(prevState => prevState.filter(e => e.id !== upc.id))
                    }
                    r(response?.resultFlag);
                    return response?.resultFlag;
                }
            }));
        })
    }, [dispatch, isMounted, removingIds])

    /**
     * Sets a product UPC as default.
     * @type {(function(*, *): void)}
     */
    const setUPCAsDefault = useCallback(async (upc) => {
        setSettingAsDefault(upc.id);
        const response = await Api.updatePartUpc({
            partUpcId: upc.id,
            code: upc.code,
            isDefault: true,
        })
        if (!isMounted())
            return;
        setSettingAsDefault(undefined);
        if (response?.resultFlag) {
            setUpcs(prevState => prevState.map(e => ({
                ...e,
                isDefault: e.id === upc.id,
            })));
            setPartUPC(upc.code);
        }
    }, [isMounted])

    /**
     * Opens the edit code dialog.
     * @type {(function(*, *): void)}
     */
    const openEditUPCDialog = useCallback((upc) => {
        setEditUpcDialogState({
            open: true,
            upc: upc,
            onEdit: (newUPC) => {
                setUpcs(prevState => prevState.map(e => e.id === newUPC.id ? {e, ...newUPC} : e))
            },
        })
    }, [])

    return (
        <>
            {
                loading &&
                Array.from({length: 1}).map((_, index) => (
                    <Grid xs={12} key={`loading-group-${index}`}>
                        <div className={'part-info-paper p-2 h-100'}>
                            <Skeleton>
                                <Typography variant={'h6'} className={'text-center'}>
                                    This is a UPC code
                                </Typography>
                            </Skeleton>
                            <div className={'d-flex flex-wrap p-2'} style={{height: 'fit-content', gap: 8}}>
                                {
                                    Array.from({length: 15}).map((_, _index) => (
                                        <Skeleton
                                            key={`loading-code-${_index}`}
                                            variant={'rounded'}
                                            width={100}
                                            height={36}
                                            sx={{transform: 'none', borderRadius: 2}}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    </Grid>
                ))
            }
            {
                !loading &&
                <Grid xs={12} key={`loading-upcs`}>
                    <Box className={'part-info-paper p-3 h-100'}>
                        <ConfigurableInfoBlock
                            title={
                                <Box component={'span'} className={'d-flex flex-column'} sx={{gap: 0.5}}>
                                    <Box component={'span'} className={'d-flex align-items-center'}>
                                        UPCs
                                        {
                                            upcs?.length > 0 &&
                                            <CopyToClipboard>
                                                {({copy}) =>
                                                    <IconButton
                                                        size={'small'}
                                                        color={'paper.primary'}
                                                        className={'ml-1'}
                                                        onPressed={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            copy(upcs.map(e => e.code).join(splitter));
                                                        }}
                                                    >
                                                        <ContentCopy className={'icon small'}/>
                                                    </IconButton>
                                                }
                                            </CopyToClipboard>
                                        }
                                    </Box>
                                    <Typography component={'span'} variant={'body2'}>
                                        Add the UPCs that are associated with this product. You can add multiple UPCs at once by separating them with the specified seperator.
                                    </Typography>
                                </Box>
                            }
                            className={'h-100'}
                            showHistoryIcon={false}
                            feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                            onValueChanged={(value) => createUPC(value)}
                            inputProps={{
                                placeholder: `New UPC`,
                                icon: <EnterIcon className={'enter-icon'}/>,
                                removeOnCommit: true,
                                commitOnEnterPress: true,
                                commitOnIconClick: true,
                                disabled: creating,
                                size: 'small',
                            }}
                        >
                            <Box
                                sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', marginBlock: 2, width: '100%'}}
                                className={'flex-fill'}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        padding: 1.5,
                                        gap: 1,
                                        height: 'fit-content',
                                        minHeight: 200,
                                        alignContent: 'flex-start',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    {
                                        upcs.map((upc, i, array) => {
                                            const numDuplicates = array.filter(e => e.code === upc.code).length;
                                            const duplicate = numDuplicates > 1;
                                            return (
                                                <Tooltip key={upc.id} title={duplicate ? `This code has ${numDuplicates - 1} duplicate${numDuplicates - 1 === 1 ? '' : 's'}` : ''}>
                                                    <Box
                                                        component={'span'}
                                                        sx={(theme) => ({
                                                            height: 'fit-content',
                                                            ...(duplicate && {
                                                                '.chips-component.solid': {
                                                                    ...(theme.palette.mode === 'dark' && {
                                                                        backgroundColor: 'rgb(255 255 0 / 20%)',
                                                                        borderColor: 'warning.main',
                                                                    }),
                                                                    ...(theme.palette.mode === 'light' && {
                                                                        backgroundColor: 'warning.light',
                                                                        borderColor: 'warning.light',
                                                                    }),
                                                                    border: '2px solid transparent',
                                                                    "p": {
                                                                        color: 'yellow',
                                                                    },
                                                                },
                                                            })
                                                        })}
                                                    >
                                                        <Chips
                                                            contentType={ChipsContentType.element}
                                                            type={settingAsDefault === upc.id
                                                                ? ChipsTypes.loading
                                                                : ChipsTypes.remove
                                                            }
                                                            iconPosition={ChipsIconPosition.start}
                                                            disabled={creating || settingAsDefault === upc.id}
                                                            onIconClick={(e) => e.stopPropagation() || e.preventDefault() || removeUPC(upc)}
                                                            buttonProps={{size: 'small', color: 'paper.primary'}}
                                                            content={(hovering) =>
                                                                <ProductChipsUPCContent
                                                                    canCopy
                                                                    code={upc}
                                                                    buttonProps={{size: 'small', color: 'paper.primary'}}
                                                                    hovering={hovering}
                                                                    settingAsDefault={settingAsDefault === upc.id}
                                                                    setAsDefault={(code) => setUPCAsDefault(code)}
                                                                    onEditUPC={(code) => openEditUPCDialog(code)}
                                                                />
                                                            }
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            )
                                        })
                                    }
                                </Box>
                            </Box>
                        </ConfigurableInfoBlock>
                    </Box>
                </Grid>
            }
            <EditUPCDialog
                {...editUpcDialogState}
                onClose={() => setEditUpcDialogState(initialEditUpcDialogState)}
            />
        </>
    );
}

export default ProductInformationUPCsViewUPCs;


const EditUPCDialog = ({open, upc, onEdit, onClose}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => (
        Yup.object().shape({
            code: Yup.string().trim().nullable().required(ValidateMessages.required),
        })
    ), [])

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        code: upc?.code ?? '',
    }), [upc])

    /**
     * Updates the code.
     * @type {(function(*): Promise<void>)}
     */
    const updateUPC = useCallback(async (values) => {
        setSubmitting(true);
        const response = await Api.updatePartUpc({
            partUpcId: upc.id,
            code: values.code,
            isDefault: upc.isDefault,
        })
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onEdit({
                ...upc,
                code: values.code,
                isDefault: upc.isDefault,
            });
            onClose();
        }
    }, [upc, isMounted, onClose, onEdit])

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'sm'}
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Edit UPC
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={updateUPC}
                            id={'upsert-code-dialog-form'}
                            validate={validate}
                            initialValues={initialValues}
                            render={() => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <MuiRFF.TextField
                                                name={'code'}
                                                label={'UPC'}
                                                required={required.code}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => onClose()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'upsert-code-dialog-form'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {

                                    submitting ? "Updating..." : "Update"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}

